import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction, 
  IconButton, 
  Paper, 
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useAdminEmails } from '../../hooks/useAdminEmails';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

// Get the current environment
const isProduction = process.env.NODE_ENV === 'production';
const envPrefix = isProduction ? 'prod' : 'dev';

/**
 * Component for managing admin emails
 * Only visible to users who are already admins
 */
export const AdminEmailsManager: React.FC = () => {
  const { adminEmails, loading, error, updateAdminEmails } = useAdminEmails();
  const [newEmail, setNewEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [environment, setEnvironment] = useState<'global' | 'dev' | 'prod'>(isProduction ? 'prod' : 'dev');
  const [devAdminEmails, setDevAdminEmails] = useState<string[]>([]);
  const [prodAdminEmails, setProductionAdminEmails] = useState<string[]>([]);
  const [globalAdminEmails, setGlobalAdminEmails] = useState<string[]>([]);
  const [loadingEnvEmails, setLoadingEnvEmails] = useState(true);
  
  // Fetch all environment admin emails
  useEffect(() => {
    async function fetchAllEnvironmentEmails() {
      setLoadingEnvEmails(true);
      try {
        // Fetch global admin emails
        const globalDocRef = doc(db, 'admin_config', 'admin_emails');
        const globalDocSnap = await getDoc(globalDocRef);
        if (globalDocSnap.exists()) {
          const data = globalDocSnap.data();
          setGlobalAdminEmails(Array.isArray(data.emails) ? data.emails : []);
        }
        
        // Fetch dev admin emails
        const devDocRef = doc(db, 'admin_config', 'dev_admin_emails');
        const devDocSnap = await getDoc(devDocRef);
        if (devDocSnap.exists()) {
          const data = devDocSnap.data();
          setDevAdminEmails(Array.isArray(data.emails) ? data.emails : []);
        }
        
        // Fetch production admin emails
        const prodDocRef = doc(db, 'admin_config', 'prod_admin_emails');
        const prodDocSnap = await getDoc(prodDocRef);
        if (prodDocSnap.exists()) {
          const data = prodDocSnap.data();
          setProductionAdminEmails(Array.isArray(data.emails) ? data.emails : []);
        }
      } catch (err) {
        console.error('Error fetching environment admin emails:', err);
      } finally {
        setLoadingEnvEmails(false);
      }
    }
    
    fetchAllEnvironmentEmails();
  }, []);

  // Get the current active email list based on environment
  const getCurrentEmailList = () => {
    switch (environment) {
      case 'dev':
        return devAdminEmails;
      case 'prod':
        return prodAdminEmails;
      case 'global':
        return globalAdminEmails;
      default:
        return adminEmails;
    }
  };
  
  // Update the appropriate environment's admin email list
  const updateEnvironmentEmails = async (emails: string[]) => {
    const docId = environment === 'global' ? 'admin_emails' : `${environment}_admin_emails`;
    await setDoc(doc(db, 'admin_config', docId), { emails });
    
    // Update the state for the specific environment
    if (environment === 'global') {
      setGlobalAdminEmails(emails);
    } else if (environment === 'dev') {
      setDevAdminEmails(emails);
    } else if (environment === 'prod') {
      setProductionAdminEmails(emails);
    }
    
    // If we're updating the current environment's emails, also update the main adminEmails
    if ((isProduction && environment === 'prod') || (!isProduction && environment === 'dev')) {
      updateAdminEmails(emails);
    }
  };

  const handleAddEmail = async () => {
    // Basic email validation
    if (!newEmail || !newEmail.includes('@') || !newEmail.includes('.')) {
      setValidationError('Please enter a valid email address');
      return;
    }

    const currentEmails = getCurrentEmailList();
    
    // Check if email already exists
    if (currentEmails.includes(newEmail)) {
      setValidationError('This email is already in the admin list');
      return;
    }

    try {
      // Add the new email to the list
      const updatedEmails = [...currentEmails, newEmail];
      await updateEnvironmentEmails(updatedEmails);
      setNewEmail('');
      setValidationError('');
      setSuccess(true);
    } catch (err: any) {
      setValidationError(err.message || 'Failed to add email');
    }
  };

  const handleRemoveEmail = async (email: string) => {
    const currentEmails = getCurrentEmailList();
    
    // Prevent removing the last admin
    if (currentEmails.length <= 1) {
      setValidationError('Cannot remove the last admin email');
      return;
    }

    try {
      const updatedEmails = currentEmails.filter(e => e !== email);
      await updateEnvironmentEmails(updatedEmails);
      setSuccess(true);
    } catch (err: any) {
      setValidationError(err.message || 'Failed to remove email');
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
      <Typography variant="h6" gutterBottom>
        Manage Admin Access
      </Typography>
      
      <Box sx={{ display: 'flex', mb: 2 }}>
        <Button 
          variant={environment === 'global' ? 'contained' : 'outlined'}
          onClick={() => setEnvironment('global')}
          sx={{ mr: 1 }}
        >
          Global
        </Button>
        <Button 
          variant={environment === 'dev' ? 'contained' : 'outlined'}
          onClick={() => setEnvironment('dev')}
          sx={{ mr: 1 }}
        >
          Development
        </Button>
        <Button 
          variant={environment === 'prod' ? 'contained' : 'outlined'}
          onClick={() => setEnvironment('prod')}
        >
          Production
        </Button>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {environment === 'global' ? 'Global admin emails apply to all environments' : 
         environment === 'dev' ? 'Development admin emails only apply in development mode' : 
         'Production admin emails only apply in production mode'}
      </Typography>
      
      <Divider sx={{ my: 2 }} />
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {validationError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', mb: 2 }}>
        <TextField
          label="New Admin Email"
          variant="outlined"
          fullWidth
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          disabled={loading}
          size="small"
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddEmail}
          disabled={loading || !newEmail}
          sx={{ ml: 1 }}
        >
          Add
        </Button>
      </Box>
      
      <Typography variant="subtitle2" gutterBottom>
        Current Admin Emails:
      </Typography>
      
      <List>
        {loadingEnvEmails ? (
          <ListItem>
            <ListItemText primary="Loading admin emails..." />
          </ListItem>
        ) : getCurrentEmailList().map((email) => (
          <ListItem key={email} divider>
            <ListItemText primary={email} />
            <ListItemSecondaryAction>
              <IconButton 
                edge="end" 
                aria-label="delete" 
                onClick={() => handleRemoveEmail(email)}
                disabled={loading || adminEmails.length <= 1}
              >
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {adminEmails.length === 0 && !loading && (
          <ListItem>
            <ListItemText primary="No admin emails configured" />
          </ListItem>
        )}
        {loading && (
          <ListItem>
            <ListItemText primary="Loading..." />
          </ListItem>
        )}
      </List>
      
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        message="Admin list updated successfully"
      />
    </Paper>
  );
};
